(ns hypnos.config
  "Hypnos configuration."
  (:require [clojure.data :as data]
            [clojure.string :as string]
            [util.config :as config]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

;; because clj-kondo linter can't see vars defined by macros
(declare
  developer-zone?
  migrate-users?
  FEATURE-DATOMIC-LOCAL
  FEATURE-TEST-ENV
  FEATURE-DEBUG-UI
  FEATURE-DEBUG-AMPLIFY
  ENV
  ZONE
  AWS-REGION
  BACKOFFICE-SUBDOMAIN
  DME-SUBDOMAIN
  IRIS-SUBDOMAIN
  SLEEPLAB-SUBDOMAIN
  BACKOFFICE-URL
  DATA-BUCKET
  DATOMIC-SYSTEM-NAME
  DATOMIC-ENDPOINT-URL
  DATOMIC-PRIMARY-ENDPOINT-URL
  DATOMIC-DB-NAME
  IRIS-PHONE-NUMBER
  URL-DOMAIN
  URL-PROTOCOL
  REST-URL
  DEMETER-URL
  COGNITO-MIGRATE-USERS
  APPSYNC-GRAPHQL-URL
  APPSYNC-API-KEY
  APPSYNC-IDENTITY-POOL
  APPSYNC-USER-POOL
  APPSYNC-MIGRATE-USER-POOL
  APPSYNC-USER-POOL-WEBCLIENT
  APPSYNC-MIGRATE-USER-POOL-WEBCLIENT
  APPSYNC-ADMIN-USER-POOL-GROUP
  APPSYNC-SUPERUSER-USER-POOL-GROUP
  APPSYNC-AGENT-USER-POOL-GROUP
  APPSYNC-AGENT-MIGRATE-USER-POOL-GROUP
  ORG-SETTINGS-TABLE
  WEBHOOK-QUEUE-URL)

(def BASE-VERSION                "7.6.0")
(def EXCHANGE-DAYS               -45) ;; number of days for the exchange window
(def DAYS-TO-UPLOAD-PORTRAIT     -7)  ;; number of days the patient can upload an image
(def PAGE-SIZE                   30)
(def DEFAULT-SETTINGS-ORG-ID     "ffffffff-ffff-ffff-ffff-ffffffffffff")

(config/def
  :ssm-param-path ["/olympus/%s/hypnos/" ZONE]
  FEATURE-DATOMIC-LOCAL                         "false"
  FEATURE-TEST-ENV                              "false"
  FEATURE-DEBUG-UI                              ""
  FEATURE-DEBUG-AMPLIFY                         ""
  ENV                                           :required
  ZONE                                          :required
  AWS-REGION                                    :required
  BACKOFFICE-SUBDOMAIN                          "backoffice" ; TODO: remove once transition is done
  DME-SUBDOMAIN                                 "dme" ; TODO: read from ParameterStore
  IRIS-SUBDOMAIN                                "iris" ; TODO update ParameterStore value
  SLEEPLAB-SUBDOMAIN                            "sleeplab" ; TODO: read from ParameterStore
  BACKOFFICE-URL                                :required
  DATA-BUCKET                                   :required
  DATOMIC-SYSTEM-NAME                           :required
  ;;DATOMIC-PRIMARY-ENDPOINT-URL                  :required
  DATOMIC-ENDPOINT-URL                          :required
  DATOMIC-DB-NAME                               :required
  IRIS-PHONE-NUMBER                             :required
  URL-DOMAIN                                    :required
  URL-PROTOCOL                                  :required
  REST-URL                                      :required
  DEMETER-URL                                   :required
  COGNITO-MIGRATE-USERS                         :required
  APPSYNC-GRAPHQL-URL                           :required
  APPSYNC-API-KEY                               :required
  APPSYNC-IDENTITY-POOL                         :required
  APPSYNC-USER-POOL                             :required
  APPSYNC-MIGRATE-USER-POOL                     :required
  APPSYNC-USER-POOL-WEBCLIENT                   :required
  APPSYNC-MIGRATE-USER-POOL-WEBCLIENT           :required
  APPSYNC-ADMIN-USER-POOL-GROUP                 :required
  APPSYNC-SUPERUSER-USER-POOL-GROUP             :required
  APPSYNC-AGENT-USER-POOL-GROUP                 :required
  APPSYNC-AGENT-MIGRATE-USER-POOL-GROUP         :required
  ORG-SETTINGS-TABLE                            :required
  WEBHOOK-QUEUE-URL                             :required)

(def PROD-URL-DOMAIN
  (string/join "." (take-last 2 (string/split URL-DOMAIN #"\."))))

(defn consult-mask-fit-url
  [consult-id]
  (str DEMETER-URL "/consult/mask-fit/" consult-id))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn url-subdomain
  [hostname]
  (let [hostname-parts    (reverse (string/split hostname #"\."))
        domain-url-parts  (reverse (string/split URL-DOMAIN #"\."))
        subdomain-parts   (first (data/diff hostname-parts domain-url-parts))]
    (last (keep identity subdomain-parts))))

(defn hypnos-url
  [subdomain]
  (if subdomain
    (str URL-PROTOCOL subdomain "." PROD-URL-DOMAIN)
    (str URL-PROTOCOL PROD-URL-DOMAIN)))

(def datomic-local?
  (= FEATURE-DATOMIC-LOCAL "true"))

(def dev-env?
  (= ENV "dev"))

(def main-zone?
  (= ENV ZONE))

(def developer-zone?
  (and dev-env? (not main-zone?)))

(def test-env?
  (= FEATURE-TEST-ENV "true"))

(def debug?
  (if dev-env?
    (#{"" "true"} FEATURE-DEBUG-UI)
    (= FEATURE-DEBUG-UI "true")))

(def debug-amplify?
  (if dev-env?
    (#{"" "true"} FEATURE-DEBUG-AMPLIFY)
    (= FEATURE-DEBUG-AMPLIFY "true")))

(def migrate-users?
  (= COGNITO-MIGRATE-USERS "true"))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; API

(defn ALLOWED-ORIGINS
  [s]
  (or developer-zone?
      (and s (let [proto   (string/replace s #"://.*$" "://")
                   parts   (-> (string/replace s #"^[^:]*://" "") (string/split #"\."))
                   domain  (->> (take-last 2 parts) (string/join "."))]
               (= [proto domain] [URL-PROTOCOL PROD-URL-DOMAIN])))))

(def AMPLIFY
  {:aws_appsync_graphqlEndpoint    APPSYNC-GRAPHQL-URL
   :aws_appsync_region             AWS-REGION
   :aws_appsync_authenticationType "AMAZON_COGNITO_USER_POOLS"
   :aws_appsync_apiKey             APPSYNC-API-KEY
   :Auth                           {:identityPoolId      APPSYNC-IDENTITY-POOL
                                    :region              AWS-REGION
                                    :userPoolId          APPSYNC-USER-POOL
                                    :userPoolWebClientId APPSYNC-USER-POOL-WEBCLIENT
                                    :mandatorySignIn     true}
   :API                            {:endpoints [{:name     "HypnosAPI"
                                                 :endpoint REST-URL}]}})

(def DATADOG
  {:applicationId      "65518ed4-40c2-4c21-9c82-1f9abc522d05"
   :clientToken        "pubacf913d2a09b3c36cf825d1b67bb9a17"
   :site               "datadoghq.com"
   :service            "hypnos"
   :env                (if main-zone? ENV (str ENV "-" ZONE))
   :version            BASE-VERSION
   :sampleRate         100
   :trackInteractions  true})

(def VERSION
  (str BASE-VERSION (if (= [ENV ZONE] ["prod" "prod"]) "" (str "-" ENV "-" ZONE))))
